import { Heading } from '@lmc/ui-kit'
import { Divider } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import UnpublishedWarning from '../../components/UnpublishedWarning'
import Warning, { WarningType } from './Warning'

type WarningsWidgetProps = {
  todayMonitoring: any
}

const WarningsWidget = ({ todayMonitoring }: WarningsWidgetProps) => {
  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: ['rota_warnings_email_notifications', 'geofencing']
  })?.data

  const flagsRotaWarningsEmailNotifications =
    flags?.rota_warnings_email_notifications?.enabled

  const { hasRota } = useHomeType()
  const home = useSelector(
    (state: { global: { home: any } }) => state.global.home
  )
  const geofencingEnabled = flags?.geofencing?.enabled
  const isGeofencingEnabled =
    (hasRota ?? false) &&
    (home?.rotaSettings as { geofencing?: boolean } | undefined)?.geofencing ===
      true &&
    geofencingEnabled === true

  const validWarningKeys = Object.values(WarningType)
    .filter(
      (key) =>
        key !== WarningType.NotYetClockedIn ||
        flagsRotaWarningsEmailNotifications
    )
    .filter(
      (key) =>
        isGeofencingEnabled ||
        (key !== WarningType.ClockInLocationMismatch &&
          key !== WarningType.ClockOutLocationMismatch)
    )

  const warnings = Object.entries(todayMonitoring.warnings).filter(([key]) =>
    validWarningKeys.includes(key as WarningType)
  ) as [string, any][]

  const warningsWithCount = warnings.filter(([, warning]) => warning.count > 0)
  const warningsWithoutCount = warnings.filter(
    ([, warning]) => warning.count === 0
  )

  return (
    <Wrapper>
      <Heading level={3}>{t('WARNINGS')}</Heading>
      <StyledDivider />
      <TotalWarnings>
        <Heading level={5}>{t('TOTAL_WARNINGS_TODAY')}</Heading>
        <Heading level={5} weight="medium">
          {todayMonitoring.warnings.totalWarnings}
        </Heading>
      </TotalWarnings>
      <StyledDivider />
      {todayMonitoring.statusSummary?.unPublishedChanges > 0 && (
        <>
          <UnpublishedWarning
            events={todayMonitoring.statusSummary.unPublishedChanges}
          />
          <StyledDivider />
        </>
      )}

      {warningsWithCount.map(([key, warning]) => (
        <Warning key={key} warningType={key as WarningType} warning={warning} />
      ))}
      {warningsWithCount.length > 0 && <StyledDivider />}
      {warningsWithoutCount.map(([key, warning]) => (
        <Warning key={key} warningType={key as WarningType} warning={warning} />
      ))}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: ${(props) => props.theme.color.neutral.color11};
  padding: 24px;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
  min-width: 0;
  flex: 2;
`

const StyledDivider = styled(Divider)`
  margin: 0;
`
const TotalWarnings = styled.div`
  display: flex;
  justify-content: space-between;
`

export default WarningsWidget
